import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useFamily } from '../../contexts/FamilyContext'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import Badge from '../ui/Badge'
import Card from '../ui/Card'
import taskTemplates, { tasksByAge, taskDifficulty, getRandomTasks, getAgeAppropriateTasks, getTasksByDifficulty } from '../../data/taskTemplates'
import toast from 'react-hot-toast'

const { FiX, FiPlus, FiStar, FiClock, FiSearch, FiFilter, FiZap, FiUsers, FiTrendingUp } = FiIcons

const TaskTemplateModal = ({ isOpen, onClose }) => {
  const { createTask } = useFamily()
  const { family } = useAuth()
  const [loading, setLoading] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('daily')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedDifficulty, setSelectedDifficulty] = useState('all')
  const [selectedAge, setSelectedAge] = useState('all')
  const [selectedTasks, setSelectedTasks] = useState([])

  // Get all available tasks
  const allTasks = useMemo(() => {
    let tasks = []
    
    if (selectedCategory === 'age-appropriate' && selectedAge !== 'all') {
      tasks = getAgeAppropriateTasks(parseInt(selectedAge))
    } else if (selectedCategory === 'difficulty' && selectedDifficulty !== 'all') {
      tasks = getTasksByDifficulty(selectedDifficulty)
    } else if (selectedCategory in taskTemplates) {
      tasks = taskTemplates[selectedCategory]
    } else if (selectedCategory === 'all') {
      tasks = Object.values(taskTemplates).flat()
    }

    // Apply search filter
    if (searchTerm) {
      tasks = tasks.filter(task => 
        task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.category.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    return tasks
  }, [selectedCategory, selectedAge, selectedDifficulty, searchTerm])

  const categories = [
    { id: 'daily', label: 'Daily Tasks', icon: FiClock, count: taskTemplates.daily.length },
    { id: 'weekly', label: 'Weekly Tasks', icon: FiTrendingUp, count: taskTemplates.weekly.length },
    { id: 'kitchen', label: 'Kitchen & Dishes', icon: '🍽️', count: taskTemplates.kitchen.length },
    { id: 'laundry', label: 'Laundry & Clothes', icon: '👕', count: taskTemplates.laundry.length },
    { id: 'outdoor', label: 'Outdoor & Yard', icon: '🌱', count: taskTemplates.outdoor.length },
    { id: 'pets', label: 'Pet Care', icon: '🐕', count: taskTemplates.pets.length },
    { id: 'organization', label: 'Organization', icon: '📦', count: taskTemplates.organization.length },
    { id: 'homework', label: 'Education & Homework', icon: '📚', count: taskTemplates.homework.length },
    { id: 'seasonal', label: 'Seasonal & Special', icon: '🎄', count: taskTemplates.seasonal.length },
    { id: 'technology', label: 'Technology & Digital', icon: '💻', count: taskTemplates.technology.length },
    { id: 'age-appropriate', label: 'Age-Appropriate', icon: FiUsers, count: 0 },
    { id: 'difficulty', label: 'By Difficulty', icon: FiZap, count: 0 },
    { id: 'all', label: 'All Tasks', icon: FiStar, count: Object.values(taskTemplates).flat().length }
  ]

  const handleTaskToggle = (task) => {
    setSelectedTasks(prev => {
      const isSelected = prev.find(t => t.title === task.title)
      if (isSelected) {
        return prev.filter(t => t.title !== task.title)
      } else {
        return [...prev, task]
      }
    })
  }

  const handleAddTasks = async () => {
    if (selectedTasks.length === 0) {
      toast.error('Please select at least one task to add')
      return
    }

    setLoading(true)
    let successCount = 0
    let errorCount = 0

    try {
      for (const task of selectedTasks) {
        const taskData = {
          title: task.title,
          description: task.description,
          points: task.points,
          category: task.category,
          estimated_time_minutes: task.estimatedTime,
          recurring_pattern: 'daily', // Default to daily, user can change later
          start_date: new Date().toISOString().split('T')[0],
          family_id: family.id
        }

        const { error } = await createTask(taskData)
        if (error) {
          console.error('Error creating task:', error)
          errorCount++
        } else {
          successCount++
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully added ${successCount} task${successCount !== 1 ? 's' : ''}! 🎉`)
      }
      if (errorCount > 0) {
        toast.error(`Failed to add ${errorCount} task${errorCount !== 1 ? 's' : ''}`)
      }

      if (successCount > 0) {
        setSelectedTasks([])
        onClose()
      }
    } catch (error) {
      console.error('Unexpected error adding tasks:', error)
      toast.error('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const formatTimeDisplay = (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`
    }
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
  }

  const getDifficultyColor = (points) => {
    if (points >= 30) return 'text-red-500'
    if (points >= 20) return 'text-orange-500'
    if (points >= 10) return 'text-yellow-500'
    return 'text-green-500'
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden"
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Task Templates Library
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="p-2"
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </Button>
              </div>

              {/* Search and Filters */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search tasks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                {selectedCategory === 'age-appropriate' && (
                  <select
                    value={selectedAge}
                    onChange={(e) => setSelectedAge(e.target.value)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="all">All Ages</option>
                    <option value="4">Ages 3-5</option>
                    <option value="7">Ages 6-8</option>
                    <option value="10">Ages 9-12</option>
                    <option value="15">Ages 13+</option>
                  </select>
                )}

                {selectedCategory === 'difficulty' && (
                  <select
                    value={selectedDifficulty}
                    onChange={(e) => setSelectedDifficulty(e.target.value)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="all">All Difficulties</option>
                    <option value="beginner">Beginner (5-15 pts)</option>
                    <option value="intermediate">Intermediate (15-25 pts)</option>
                    <option value="advanced">Advanced (25-40 pts)</option>
                    <option value="expert">Expert (40+ pts)</option>
                  </select>
                )}
              </div>
            </div>

            <div className="flex h-[calc(90vh-200px)]">
              {/* Categories Sidebar */}
              <div className="w-80 border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Categories</h3>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all duration-200 ${
                        selectedCategory === category.id
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border-l-4 border-primary-500'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {typeof category.icon === 'string' ? (
                        <span className="text-xl">{category.icon}</span>
                      ) : (
                        <SafeIcon icon={category.icon} className="text-lg" />
                      )}
                      <div className="flex-1">
                        <div className="font-medium">{category.label}</div>
                        {category.count > 0 && (
                          <div className="text-xs opacity-75">{category.count} tasks</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tasks Grid */}
              <div className="flex-1 p-6 overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Available Tasks ({allTasks.length})
                  </h3>
                  {selectedTasks.length > 0 && (
                    <Badge variant="primary" className="flex items-center gap-1">
                      <SafeIcon icon={FiPlus} className="text-sm" />
                      {selectedTasks.length} selected
                    </Badge>
                  )}
                </div>

                {allTasks.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {allTasks.map((task, index) => {
                      const isSelected = selectedTasks.find(t => t.title === task.title)
                      return (
                        <motion.div
                          key={`${task.title}-${index}`}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          whileHover={{ scale: 1.02 }}
                        >
                          <Card
                            className={`cursor-pointer transition-all duration-200 ${
                              isSelected
                                ? 'ring-2 ring-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                : 'hover:shadow-lg'
                            }`}
                            onClick={() => handleTaskToggle(task)}
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <div className="text-2xl">{task.emoji}</div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                                  {task.title}
                                </h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                  {task.description}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 mb-3">
                              <Badge variant="primary" size="sm" className="flex items-center gap-1">
                                <SafeIcon icon={FiStar} className={`text-xs ${getDifficultyColor(task.points)}`} />
                                {task.points} pts
                              </Badge>
                              <Badge variant="secondary" size="sm" className="flex items-center gap-1">
                                <SafeIcon icon={FiClock} className="text-xs" />
                                {formatTimeDisplay(task.estimatedTime)}
                              </Badge>
                              <Badge variant="info" size="sm" className="capitalize">
                                {task.category}
                              </Badge>
                            </div>

                            {isSelected && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="flex items-center justify-center p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg"
                              >
                                <SafeIcon icon={FiPlus} className="text-primary-600 dark:text-primary-400" />
                                <span className="text-sm font-medium text-primary-600 dark:text-primary-400 ml-2">
                                  Selected
                                </span>
                              </motion.div>
                            )}
                          </Card>
                        </motion.div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🔍</div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      No tasks found
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Try adjusting your search or selecting a different category
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedTasks.length > 0 ? (
                    <>Selected {selectedTasks.length} task{selectedTasks.length !== 1 ? 's' : ''} • 
                    Total: {selectedTasks.reduce((sum, task) => sum + task.points, 0)} points</>
                  ) : (
                    'Select tasks to add to your family'
                  )}
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="ghost"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleAddTasks}
                    loading={loading}
                    disabled={selectedTasks.length === 0}
                  >
                    <SafeIcon icon={FiPlus} className="text-sm" />
                    Add {selectedTasks.length > 0 ? selectedTasks.length : ''} Task{selectedTasks.length !== 1 ? 's' : ''}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default TaskTemplateModal